import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, RefreshCw, Home, X } from 'lucide-react';
import './ErrorDisplay.css';

interface ErrorDisplayProps {
  title?: string;
  message?: string;
  error?: Error | string;
  type?: 'error' | 'warning' | 'info';
  fullScreen?: boolean;
  showRetry?: boolean;
  showHome?: boolean;
  showClose?: boolean;
  onRetry?: () => void;
  onHome?: () => void;
  onClose?: () => void;
  className?: string;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  title = 'Something went wrong',
  message,
  error,
  type = 'error',
  fullScreen = false,
  showRetry = true,
  showHome = false,
  showClose = false,
  onRetry,
  onHome,
  onClose,
  className = ''
}) => {
  const containerVariants = {
    initial: { opacity: 0, y: 20 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 }
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: { duration: 0.2 }
    }
  };

  const iconVariants = {
    initial: { scale: 0 },
    animate: {
      scale: 1,
      transition: {
        type: "spring" as const,
        stiffness: 200,
        damping: 15
      }
    }
  };

  const getErrorMessage = (): string => {
    if (message) return message;
    if (error) {
      if (typeof error === 'string') return error;
      return error.message || 'An unexpected error occurred';
    }
    return 'An unexpected error occurred. Please try again.';
  };

  const getIconByType = () => {
    switch (type) {
      case 'error':
        return <AlertCircle className="error-display__icon error-display__icon--error" size={48} />;
      case 'warning':
        return <AlertCircle className="error-display__icon error-display__icon--warning" size={48} />;
      case 'info':
        return <AlertCircle className="error-display__icon error-display__icon--info" size={48} />;
      default:
        return <AlertCircle className="error-display__icon error-display__icon--error" size={48} />;
    }
  };

  return (
    <motion.div
      className={`error-display__container ${fullScreen ? 'error-display__container--fullscreen' : ''} ${className}`}
      variants={containerVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <div className={`error-display__content error-display__content--${type}`}>
        {showClose && onClose && (
          <button
            className="error-display__close-btn"
            onClick={onClose}
            aria-label="Close error message"
          >
            <X size={20} />
          </button>
        )}

        <motion.div
          className="error-display__icon-wrapper"
          variants={iconVariants}
          initial="initial"
          animate="animate"
        >
          {getIconByType()}
        </motion.div>

        <div className="error-display__text">
          <h2 className="error-display__title">{title}</h2>
          <p className="error-display__message">{getErrorMessage()}</p>
        </div>

        <div className="error-display__actions">
          {showRetry && onRetry && (
            <motion.button
              className="error-display__button error-display__button--primary"
              onClick={onRetry}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <RefreshCw size={18} />
              <span>Try Again</span>
            </motion.button>
          )}

          {showHome && onHome && (
            <motion.button
              className="error-display__button error-display__button--secondary"
              onClick={onHome}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Home size={18} />
              <span>Go Home</span>
            </motion.button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ErrorDisplay;
